import React, {useEffect, useRef, useState} from 'react'
import styled, {useTheme} from 'styled-components'

const appendScript: (src: string) => void = async src => {
  if (document.querySelectorAll(`script[src="${src}"]`).length) {
    return
  }

  const tag = document.createElement('script')
  tag.src = src
  tag.async = true
  document.body.appendChild(tag)
}

interface VideoProps {
  thumbnail?: string
  thumbnailAlt?: string
  id: string
  containerId?: string
  controls?: boolean
  togglePlay?: boolean
  time?: number
}

const KEYBOARD_KEYS = {
  SPACE: ' ',
  ENTER: 'Enter',
}

export interface WistiaApi {
  bind: (event: 'play', handler: () => void) => void
  unbind: (event: 'play', handler: () => void) => void
  remove: () => void
  play(): void
  pause(): void
  time(time: number): void
}

interface WistiaEl extends HTMLDivElement {
  wistiaApi?: WistiaApi
}

interface Wq {
  id: string
  onReady: (video: WistiaApi) => void
}

declare global {
  interface Window {
    _wq: Wq[]
  }
}

const WistiaImageWrapper = styled.div`
  position: relative;

  &:focus {
    ${({theme}) => theme.normalizeStyles({...theme.focus.outlines.default})}
  }

  img {
    width: 100%;
    max-width: 100%;
    cursor: pointer;
  }
`

const WISTIA_SCRIPT_URL = '//fast.wistia.com/assets/external/E-v1.js'

const Video: React.FC<VideoProps> = ({
  thumbnail,
  thumbnailAlt,
  id,
  containerId,
  controls,
  togglePlay,
  time,
}) => {
  const wistiaEl = useRef<WistiaEl>(null)
  const [isThumbnailVisible, setIsThumbnailVisible] = useState(!!thumbnail)
  const [videoApi, setVideoApi] = useState<WistiaApi>()
  const theme = useTheme()

  const loadVideo = (autoplay = false) => {
    appendScript(`https://fast.wistia.com/embed/medias/${id}.jsonp`)
    if (!wistiaEl.current) {
      return
    }

    const wistia_embed = document.createElement('div')
    const wistia_swatch = document.createElement('div')

    wistia_embed.classList.add('wistia_embed', 'wistia_async_' + id, 'seo=false', 'videoFoam=true')

    if (autoplay) {
      wistia_embed.classList.add('autoPlay=true')
    }

    if (!controls) {
      wistia_embed.classList.add(
        'settingsControl=false',
        'controlsVisibleOnLoad=false',
        'volumeControl=false',
        'playButton=false',
        'playbar=false',
        'smallPlayButton=false',
        'qualityControl=false',
        'fullscreenButton=false',
        'playbackRateControl=false',
        'muted=true',
      )
    }

    wistia_swatch.classList.add('wistia_swatch')
    wistia_swatch.style.cssText =
      'height:100%;left:0;opacity:0;overflow:hidden;position:absolute;top:0;transition:opacity 200ms;width:100%;'

    wistiaEl.current.appendChild(wistia_embed)
    wistia_embed.appendChild(wistia_swatch)
    wistiaEl.current.setAttribute('data-testid', 'wistia-element-ref')

    if (containerId) {
      wistia_embed.setAttribute('id', containerId)
    }
  }

  // * to get access to video tag and wistia API
  useEffect(() => {
    if (!thumbnail) {
      appendScript(WISTIA_SCRIPT_URL)
      loadVideo(false)
    }

    let videoApi: WistiaApi
    const onPlay = () => {
      setIsThumbnailVisible(false)
    }

    window._wq = window._wq || []
    window._wq.push({
      id: containerId || id,
      onReady: function (video) {
        videoApi = video

        setVideoApi(video)

        if (thumbnail) {
          video.bind('play', onPlay)
        }
      },
    })

    return () => {
      if (thumbnail) {
        videoApi?.unbind('play', onPlay)
      }
      videoApi?.remove()
    }
  }, [])

  useEffect(() => {
    if (!videoApi || isThumbnailVisible) {
      return
    }

    if (typeof time !== 'undefined') {
      videoApi.time(time)
    }

    if (typeof togglePlay !== 'undefined') {
      togglePlay ? videoApi.play() : videoApi.pause()
    }
  }, [togglePlay, time, videoApi, isThumbnailVisible])

  const onClickThumbnail = () => {
    appendScript(WISTIA_SCRIPT_URL)

    if (isThumbnailVisible) {
      loadVideo(true)
    }
  }

  const getThumbnail = () => {
    return (
      <WistiaImageWrapper
        className="autoPlay=true"
        tabIndex={0}
        role="button"
        onClick={onClickThumbnail}
        onKeyPress={event => {
          if (event.key == KEYBOARD_KEYS.ENTER || event.key == KEYBOARD_KEYS.SPACE) {
            onClickThumbnail()
          }
        }}
      >
        <picture>
          <source
            srcSet={`https://embed-fastly.wistia.com/deliveries/${thumbnail}.webp?image_play_button_size=2x&image_play_button=1&image_play_button_color=050038e0&image_resize=480`}
            type="image/webp"
            media={theme.mediaQueries.v2Mobile}
          />
          <source
            srcSet={`https://embed-fastly.wistia.com/deliveries/${thumbnail}.jpg?image_play_button_size=2x&image_play_button=1&image_play_button_color=050038e0&image_resize=480`}
            type="image/jpeg"
            media={theme.mediaQueries.v2Mobile}
          />
          <source
            srcSet={`https://embed-fastly.wistia.com/deliveries/${thumbnail}.webp?image_play_button_size=2x&image_play_button=1&image_play_button_color=050038e0`}
            type="image/webp"
          />
          <source
            srcSet={`https://embed-fastly.wistia.com/deliveries/${thumbnail}.jpg?image_play_button_size=2x&image_play_button=1&image_play_button_color=050038e0`}
            type="image/jpeg"
          />
          <img
            src={`https://embed-fastly.wistia.com/deliveries/${thumbnail}.jpg?image_play_button_size=2x&image_play_button=1&image_play_button_color=050038e0`}
            alt={thumbnailAlt}
          />
        </picture>
      </WistiaImageWrapper>
    )
  }

  const getVideo = () => {
    return <div ref={wistiaEl} />
  }

  return (
    <>
      {isThumbnailVisible && getThumbnail()}
      {getVideo()}
    </>
  )
}

export default Video
